import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  MoreHorizontal,
  Edit,
  XCircle,
  Calendar,
  User,
  FileText,
  Car,
  Wrench,
  Receipt,
  ArrowRight,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { JobCardListItem, JobCardStatus, JobCardPriority, JobCardType } from '@/app/types/jobCard';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';
import { StatusBadge, PriorityBadge } from '../../components/PriorityBadge';
import React from 'react';

// Custom events for row actions
export const editJobCardEvent = 'edit-job-card';
export const cancelJobCardEvent = 'cancel-job-card';
export const convertToInvoiceEvent = 'convert-job-card-to-invoice';
export const advanceStatusEvent = 'advance-job-card-status';

// Status order for computing next status
const statusOrder: JobCardStatus[] = [
  'arrival',
  'inspection',
  'quoted',
  'approved',
  'under_service',
  'quality_check',
  'ready',
  'delivered',
];

// Status labels for display
const statusLabels: Record<string, string> = {
  arrival: 'Arrival',
  inspection: 'Inspection',
  quoted: 'Quoted',
  approved: 'Approved',
  under_service: 'Under Service',
  quality_check: 'Quality Check',
  ready: 'Ready',
  delivered: 'Delivered',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
};

// Compute the next status for a given current status
const getNextStatus = (currentStatus: JobCardStatus): { status: JobCardStatus; label: string } | null => {
  const currentIndex = statusOrder.indexOf(currentStatus);
  if (currentIndex === -1 || currentIndex >= statusOrder.length - 1) return null;
  const nextStatus = statusOrder[currentIndex + 1];
  return { status: nextStatus, label: statusLabels[nextStatus] || nextStatus };
};

// Helper function to format date
const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

// Helper function to format currency
const formatCurrency = (amount: string | number): string => {
  const numAmount = Number(amount);
  if (isNaN(numAmount)) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(numAmount);
};

// Job card type labels
const jobCardTypeLabels: Record<JobCardType, string> = {
  regular_service: 'Regular Service',
  repair: 'Repair',
  detailing: 'Detailing',
  accidental: 'Accidental',
  warranty: 'Warranty',
  pdi: 'PDI',
};

// Define custom meta type
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
  enableColumnFilter?: boolean;
};

export const columns: ExtendedColumnDef<JobCardListItem>[] = [
  {
    accessorKey: 'document_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="JC Number" />,
    cell: ({ row }): JSX.Element => (
      <span className="font-medium">{row.getValue('document_number')}</span>
    ),
    meta: {
      label: 'JC Number',
      placeholder: 'Filter JC numbers...',
      variant: 'text',
      icon: FileText,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }): JSX.Element => {
      const date = row.getValue('transaction_date') as string;
      return <span>{formatDate(date)}</span>;
    },
    meta: {
      label: 'Date',
      placeholder: 'Select date range...',
      variant: 'dateRange',
      icon: Calendar,
    },
    enableColumnFilter: true,
    enableSorting: true,
  },
  {
    id: 'age',
    header: 'Age',
    cell: ({ row }): JSX.Element => {
      const terminalStatuses: JobCardStatus[] = ['invoiced', 'cancelled', 'delivered'];
      if (terminalStatuses.includes(row.original.status)) {
        return <span className="text-muted-foreground">&mdash;</span>;
      }
      const txDate = row.original.transaction_date;
      const days = Math.floor((Date.now() - new Date(txDate).getTime()) / 86400000);
      let colorClass = '';
      if (days >= 3) colorClass = 'text-red-600 font-bold';
      else if (days >= 1) colorClass = 'text-amber-600';
      return <span className={colorClass}>{days}d</span>;
    },
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: 'estimated_completion_date',
    header: 'Est. Done',
    cell: ({ row }): JSX.Element => {
      const estDate = row.original.estimated_completion_date;
      if (!estDate) return <span className="text-muted-foreground">&mdash;</span>;
      const terminalStatuses: JobCardStatus[] = ['invoiced', 'cancelled', 'delivered'];
      const isTerminal = terminalStatuses.includes(row.original.status);
      const isOverdue = !isTerminal && new Date(estDate) < new Date();
      return (
        <span className={isOverdue ? 'text-red-600' : ''}>
          {formatDate(estDate)}
        </span>
      );
    },
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: 'customer_complaint',
    header: 'Complaint',
    cell: ({ row }): JSX.Element => {
      const complaint = row.original.customer_complaint;
      if (!complaint) return <span className="text-muted-foreground">&mdash;</span>;
      return (
        <span className="max-w-[200px] truncate block text-xs" title={complaint}>
          {complaint}
        </span>
      );
    },
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: 'vehicle.registration_number',
    accessorKey: 'vehicle.registration_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Vehicle" />,
    cell: ({ row }): JSX.Element => {
      const vehicle = row.original.vehicle;
      return (
        <div className="flex flex-col">
          <span className="font-medium">{vehicle?.registration_number || '-'}</span>
          {(vehicle?.make || vehicle?.model) && (
            <span className="text-xs text-muted-foreground">
              {vehicle.make} {vehicle.model}
            </span>
          )}
        </div>
      );
    },
    meta: {
      label: 'Vehicle',
      placeholder: 'Filter vehicles...',
      variant: 'text',
      icon: Car,
    },
    enableColumnFilter: true,
  },
  {
    id: 'party.name',
    accessorKey: 'party.name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
    cell: ({ row }): JSX.Element => {
      const party = row.original.party;
      return (
        <div className="flex flex-col">
          <span>{party?.name || '-'}</span>
          {party?.phone && <span className="text-xs text-muted-foreground">{party.phone}</span>}
        </div>
      );
    },
    meta: {
      label: 'Customer',
      placeholder: 'Filter customers...',
      variant: 'text',
      icon: User,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'job_card_type',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
    cell: ({ row }): JSX.Element => {
      const type = row.getValue('job_card_type') as JobCardType | null;
      return <span>{type ? jobCardTypeLabels[type] : '-'}</span>;
    },
    meta: {
      label: 'Type',
      placeholder: 'Filter by type...',
      variant: 'select',
      icon: Wrench,
      options: [
        { label: 'Regular Service', value: 'regular_service' },
        { label: 'Repair', value: 'repair' },
        { label: 'Detailing', value: 'detailing' },
        { label: 'Accidental', value: 'accidental' },
        { label: 'Warranty', value: 'warranty' },
        { label: 'PDI', value: 'pdi' },
      ],
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'priority',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Priority" />,
    cell: ({ row }): JSX.Element => {
      const priority = row.getValue('priority') as JobCardPriority;
      return <PriorityBadge priority={priority} />;
    },
    meta: {
      label: 'Priority',
      placeholder: 'Filter by priority...',
      variant: 'select',
      options: [
        { label: 'Low', value: 'low' },
        { label: 'Normal', value: 'normal' },
        { label: 'High', value: 'high' },
        { label: 'Urgent', value: 'urgent' },
      ],
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }): JSX.Element => {
      const status = row.getValue('status') as JobCardStatus;
      return <StatusBadge status={status} />;
    },
    meta: {
      label: 'Status',
      placeholder: 'Filter by status...',
      variant: 'select',
      options: [
        { label: 'Arrival', value: 'arrival' },
        { label: 'Inspection', value: 'inspection' },
        { label: 'Quoted', value: 'quoted' },
        { label: 'Approved', value: 'approved' },
        { label: 'Under Service', value: 'under_service' },
        { label: 'Quality Check', value: 'quality_check' },
        { label: 'Ready', value: 'ready' },
        { label: 'Delivered', value: 'delivered' },
        { label: 'Invoiced', value: 'invoiced' },
        { label: 'Cancelled', value: 'cancelled' },
      ],
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'bay_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Bay" />,
    cell: ({ row }): JSX.Element => <span>{row.getValue('bay_number') || '-'}</span>,
    enableColumnFilter: false,
  },
  {
    id: 'lead_technician.name',
    accessorKey: 'lead_technician.name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Technician" />,
    cell: ({ row }): JSX.Element => {
      const tech = row.original.lead_technician;
      return <span>{tech?.name || '-'}</span>;
    },
    meta: {
      label: 'Technician',
      placeholder: 'Filter technicians...',
      variant: 'text',
      icon: Wrench,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'total_amount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }): JSX.Element => {
      const amount = row.getValue('total_amount') as string;
      return <span className="font-medium">{formatCurrency(amount)}</span>;
    },
    enableColumnFilter: false,
  },
  {
    id: 'actions',
    cell: ({ row }): React.ReactNode => {
      const jobCard = row.original;
      const canConvert = jobCard.status === 'ready' || jobCard.status === 'delivered';
      const nextStatus = getNextStatus(jobCard.status);

      const handleEdit = (): void => {
        const event = new CustomEvent(editJobCardEvent, {
          detail: { jobCard },
        });
        document.dispatchEvent(event);
      };

      const handleCancel = (): void => {
        const event = new CustomEvent(cancelJobCardEvent, {
          detail: { jobCard },
        });
        document.dispatchEvent(event);
      };

      const handleConvert = (): void => {
        const event = new CustomEvent(convertToInvoiceEvent, {
          detail: { jobCard },
        });
        document.dispatchEvent(event);
      };

      const handleAdvanceStatus = (): void => {
        if (!nextStatus) return;
        const event = new CustomEvent(advanceStatusEvent, {
          detail: { id: jobCard.id, nextStatus: nextStatus.status },
        });
        document.dispatchEvent(event);
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {nextStatus && (
              <DropdownMenuItem onClick={handleAdvanceStatus}>
                <ArrowRight className="mr-2 h-4 w-4" />
                Move to {nextStatus.label}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            {canConvert && (
              <DropdownMenuItem onClick={handleConvert}>
                <Receipt className="mr-2 h-4 w-4" />
                Convert to Invoice
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={handleCancel}
              className="text-destructive focus:text-destructive"
              disabled={jobCard.status === 'cancelled' || jobCard.status === 'invoiced'}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
